import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Card, PageHeader } from "@/components/ui-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, ChevronLeft, ChevronRight, Trash2, Pencil, Eye, Paperclip, Wallet, Receipt } from "lucide-react";
import { money, monthKey, prettyMonth, prettyDate, todayISO } from "@/mobile/mobileUtils";
import { toast } from "sonner";

const CAT_COLORS = ["#1479C4", "#F5822A", "#5A34B4", "#0EA5A5", "#1E9BE9", "#EAB308", "#7C3AED", "#0891B2", "#DB2777", "#16A34A", "#64748B"];

function shiftMonth(key, delta) {
  let [y, m] = key.split("-").map(Number);
  m += delta;
  while (m <= 0) { m += 12; y -= 1; }
  while (m > 12) { m -= 12; y += 1; }
  return `${y}-${String(m).padStart(2, "0")}`;
}

export default function AdminExpenses() {
  const { user } = useAuth();
  const [month, setMonth] = useState(monthKey());
  const [dash, setDash] = useState(null);
  const [list, setList] = useState(null);
  const [dialog, setDialog] = useState(null); // null | {} | expense

  const load = () => {
    setDash(null); setList(null);
    api.get(`/expenses-dashboard?month=${month}`).then((r) => setDash(r.data)).catch(() => setDash({ total: 0, count: 0, by_category: [], trend: [] }));
    api.get(`/expenses?month=${month}`).then((r) => setList(r.data || [])).catch(() => setList([]));
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [month]); // eslint-disable-line

  if (user && user.role !== "admin") return <Card className="p-6" data-testid="expenses-admin-only">Only administrators can view expenses.</Card>;

  const del = async (e) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${e.id}`); toast.success("Expense deleted"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const viewReceipt = async (e) => {
    try {
      const res = await api.get(`/expenses/${e.id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open receipt"); }
  };

  const maxCat = dash?.by_category?.[0]?.amount || 1;
  const maxTrend = Math.max(1, ...(dash?.trend || []).map((t) => t.amount));

  return (
    <div>
      <PageHeader title="Expenses" subtitle="Track firm expenses and receipts by month.">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl">
            <button onClick={() => setMonth(shiftMonth(month, -1))} data-testid="wexp-month-prev" className="p-2 text-slate-500"><ChevronLeft className="h-4 w-4" /></button>
            <div className="text-sm font-semibold text-slate-700 w-32 text-center" data-testid="wexp-month-label">{prettyMonth(month)}</div>
            <button onClick={() => setMonth(shiftMonth(month, 1))} data-testid="wexp-month-next" className="p-2 text-slate-500"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <button onClick={() => setDialog({})} data-testid="wexp-add-btn" className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm"><Plus className="h-4 w-4" /> Add expense</button>
        </div>
      </PageHeader>

      {!dash ? <Loader /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <Card className="p-5" data-testid="wexp-kpi-total">
              <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-2"><Wallet className="h-5 w-5" /></div>
              <div className="font-head font-extrabold text-2xl text-slate-900">{money(dash.total, dash.currency)}</div>
              <div className="text-xs text-slate-500">Total · {prettyMonth(month)}</div>
            </Card>
            <Card className="p-5">
              <div className="h-9 w-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center mb-2"><Receipt className="h-5 w-5" /></div>
              <div className="font-head font-extrabold text-2xl text-slate-900">{dash.count}</div>
              <div className="text-xs text-slate-500">Expenses logged</div>
            </Card>
            <Card className="p-5">
              <div className="text-xs text-slate-500 mb-1">Top category</div>
              <div className="font-head font-bold text-slate-900">{dash.by_category?.[0]?.category || "—"}</div>
              <div className="text-sm text-slate-500">{dash.by_category?.[0] ? money(dash.by_category[0].amount) : "\u00A0"}</div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <Card className="p-5" data-testid="wexp-by-category">
              <div className="font-head font-bold text-slate-900 mb-3">By category</div>
              {(!dash.by_category || dash.by_category.length === 0) && <div className="text-sm text-slate-400">No expenses this month.</div>}
              <div className="space-y-2.5">
                {dash.by_category?.map((c, i) => (
                  <div key={c.category}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-600">{c.category}</span><span className="font-semibold text-slate-800">{money(c.amount)}</span></div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(c.amount / maxCat) * 100}%`, background: CAT_COLORS[i % CAT_COLORS.length] }} /></div>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-5">
              <div className="font-head font-bold text-slate-900 mb-3">6-month trend</div>
              <div className="flex items-end gap-2 h-28">
                {dash.trend?.map((t) => (
                  <div key={t.month} className="flex-1 flex flex-col items-center gap-1">
                    <div className="text-[10px] text-slate-500">{t.amount ? money(t.amount).replace(/\.00$/, "") : ""}</div>
                    <div className="w-full rounded-t bg-primary" style={{ height: `${Math.max(4, (t.amount / maxTrend) * 80)}px`, opacity: t.month === month ? 1 : 0.45 }} />
                    <div className="text-[10px] text-slate-400">{t.month.slice(5)}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}

      <Card className="overflow-hidden" data-testid="wexp-table">
        <div className="px-5 py-3 border-b border-slate-100 font-head font-bold text-slate-900">Expenses · {prettyMonth(month)}</div>
        {list === null ? <Loader /> : list.length === 0 ? (
          <div className="text-center py-12 text-slate-400"><Receipt className="h-8 w-8 mx-auto mb-2" /><div className="text-sm">No expenses in {prettyMonth(month)}.</div></div>
        ) : (
          <div className="divide-y divide-slate-100">
            {list.map((e) => (
              <div key={e.id} className="px-5 py-3.5 flex items-center gap-4" data-testid={`wexp-row-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800">{e.category}</div>
                  <div className="text-xs text-slate-400">{prettyDate(e.date)}{e.staff_name ? ` · ${e.staff_name}` : ""}{e.client_name ? ` · ${e.client_name}` : ""}{e.note ? ` · ${e.note}` : ""}</div>
                </div>
                <div className="font-head font-extrabold text-slate-900 shrink-0">{money(e.amount, e.currency)}</div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {e.has_receipt && <button onClick={() => viewReceipt(e)} data-testid={`wexp-view-receipt-${e.id}`} title="View receipt" className="p-2 rounded-lg hover:bg-slate-100 text-primary"><Eye className="h-4 w-4" /></button>}
                  <button onClick={() => setDialog(e)} data-testid={`wexp-edit-${e.id}`} title="Edit" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(e)} data-testid={`wexp-delete-${e.id}`} title="Delete" className="p-2 rounded-lg hover:bg-rose-50 text-rose-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {dialog && (
        <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
          <ExpenseDialog expense={dialog.id ? dialog : null} onDone={() => { setDialog(null); load(); }} />
        </Dialog>
      )}
    </div>
  );
}

function ExpenseDialog({ expense, onDone }) {
  const isEdit = !!expense;
  const [cats, setCats] = useState([]);
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState(isEdit
    ? { category: expense.category, amount: String(expense.amount), date: expense.date, staff_id: expense.staff_id || "", note: expense.note || "", client_id: expense.client_id || "", receipt_file_id: expense.receipt_file_id || null, receipt_filename: expense.receipt_filename || null }
    : { category: "", amount: "", date: todayISO(), staff_id: "", note: "", client_id: "", receipt_file_id: null, receipt_filename: null });
  const [clientName, setClientName] = useState(expense?.client_name || "");
  const [cq, setCq] = useState("");
  const [cresults, setCresults] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/expense-categories").then((r) => setCats(r.data || [])).catch(() => {});
    api.get("/team/staff").then((r) => setStaff(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!cq) { setCresults([]); return; }
    const t = setTimeout(() => api.get(`/clients?q=${encodeURIComponent(cq)}`).then((r) => setCresults((r.data || []).slice(0, 6))).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [cq]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/expenses/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, receipt_file_id: data.file_id, receipt_filename: data.filename }));
      toast.success("Receipt attached");
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!f.category) { toast.error("Choose a category"); return; }
    const amt = parseFloat(f.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    const payload = { ...f, amount: amt, client_id: f.client_id || null, staff_id: f.staff_id || null };
    try {
      if (isEdit) await api.put(`/expenses/${expense.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(isEdit ? "Expense updated" : "Expense added");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="wexp-dialog">
      <DialogHeader><DialogTitle>{isEdit ? "Edit expense" : "New expense"}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase">Amount (CAD)</label>
            <input data-testid="wexp-amount" type="number" inputMode="decimal" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="0.00" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase">Date</label>
            <input data-testid="wexp-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase">Category</label>
          <select data-testid="wexp-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
            <option value="">Select category…</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase">Staff (optional)</label>
          <select data-testid="wexp-staff" value={f.staff_id} onChange={(e) => setF({ ...f, staff_id: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
            <option value="">Not linked</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase">Client (optional)</label>
          {f.client_id ? (
            <div className="mt-1 flex items-center justify-between px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50">
              <span className="text-sm font-medium text-slate-800">{clientName || "Selected client"}</span>
              <button onClick={() => { setF({ ...f, client_id: "" }); setClientName(""); }} className="text-xs text-rose-500 font-semibold">Remove</button>
            </div>
          ) : (
            <div className="relative">
              <input data-testid="wexp-client-search" value={cq} onChange={(e) => setCq(e.target.value)} placeholder="Search client to link" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
              {cresults.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                  {cresults.map((c) => (
                    <button key={c.id} data-testid={`wexp-client-opt-${c.id}`} onClick={() => { setF({ ...f, client_id: c.id }); setClientName(c.full_name); setCq(""); setCresults([]); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50">{c.full_name}</button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase">Note (optional)</label>
          <textarea data-testid="wexp-note" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="What was this for?" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase">Receipt (optional)</label>
          <label data-testid="wexp-receipt-label" className="mt-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 cursor-pointer hover:bg-slate-50">
            <Paperclip className="h-4 w-4" />
            {uploading ? "Uploading…" : f.receipt_filename ? <span className="text-slate-800 font-medium truncate">{f.receipt_filename}</span> : "Attach photo or PDF"}
            <input data-testid="wexp-receipt-input" type="file" accept="image/png,image/jpeg,image/jpg,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        </div>
        <DialogFooter>
          <button onClick={save} disabled={saving || uploading} data-testid="wexp-save" className="bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm w-full disabled:opacity-60">{saving ? "Saving…" : isEdit ? "Save changes" : "Add expense"}</button>
        </DialogFooter>
      </div>
    </DialogContent>
  );
}
